import { Link } from "react-router-dom";
import { ArrowRight, Check, Phone } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import Reviews from "../components/Reviews";
import CtaBanner from "../components/CtaBanner";
import { BUSINESS, IMAGES } from "../data/business";

/**
 * Reusable service page (Nails / Podology / Face).
 * Props: service (from data/services.js), image, altImage, testId
 */
export default function ServicePageTemplate({
  service,
  heroImage,
  secondaryImage,
  breadcrumbLabel,
  bullets,
  finalSection,
  beforeCta,
}) {
  const CANONICAL_ORIGIN = "https://beautix.pl";
  const pageUrl = `${CANONICAL_ORIGIN}/${service.slug}`;

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${pageUrl}#service`,
        serviceType: service.title,
        name: service.title,
        description: service.metaDescription,
        areaServed: { "@type": "City", name: "Katowice" },
        provider: {
          "@type": "BeautySalon",
          name: BUSINESS.name,
          address: {
            "@type": "PostalAddress",
            streetAddress: BUSINESS.address.street,
            addressLocality: BUSINESS.address.city,
            postalCode: BUSINESS.address.postalCode,
            addressCountry: "PL",
          },
          telephone: BUSINESS.phones[1].tel,
          url: CANONICAL_ORIGIN,
        },
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: `${service.title} — oferta`,
          itemListElement: service.items.map((i) => ({
            "@type": "Offer",
            itemOffered: { "@type": "Service", name: i.name, description: i.desc },
          })),
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Strona główna", item: `${CANONICAL_ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: service.title, item: pageUrl },
        ],
      },
    ],
  };

  return (
    <>
      <Seo
        title={service.metaTitle}
        description={service.metaDescription}
        keywords={service.items.map((i) => i.keyword).join(", ")}
        path={`/${service.slug}`}
        image={heroImage}
        schema={schema}
      />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid={`service-hero-${service.slug}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-8 pb-4">
          <Breadcrumbs items={[{ label: breadcrumbLabel || service.title }]} />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 z-10">
            <p className="eyebrow mb-5">{service.subtitle}</p>
            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-[5rem] leading-[1.02] tracking-tight">
              {service.hero.split(" ").slice(0, -2).join(" ")}{" "}
              <span className="italic text-[var(--beautix-pink)]">
                {service.hero.split(" ").slice(-2).join(" ")}
              </span>
            </h1>
            <p className="mt-8 text-lg leading-relaxed text-[var(--beautix-muted)] max-w-xl font-light">
              {service.intro}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href={`tel:${BUSINESS.phones[1].tel}`}
                data-testid="service-call-btn"
                className="inline-flex items-center gap-3 bg-[var(--beautix-pink)] hover:bg-[var(--beautix-pink-dark)] text-white px-8 py-4 rounded-full text-sm font-medium transition-colors"
              >
                <Phone className="w-4 h-4" strokeWidth={1.5} />
                Zapisz się: {BUSINESS.phones[1].number}
              </a>
              <Link
                to="/cennik"
                className="inline-flex items-center gap-3 border border-[var(--beautix-ink)]/70 hover:bg-[var(--beautix-cream)] px-8 py-4 rounded-full text-sm font-medium transition-colors"
              >
                Cennik
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="rounded-tl-[80px] overflow-hidden aspect-[4/5] lg:aspect-[3/4]">
              <img
                src={heroImage}
                alt={`${service.title} Katowice — BEAUTIX Studio Kosmetyki Nowoczesnej`}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services list */}
      <section className="bg-[var(--beautix-cream)] py-24" data-testid={`service-items-${service.slug}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 items-end">
            <div className="lg:col-span-7">
              <p className="eyebrow mb-4">Zakres zabiegów</p>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05]">
                Co dla Ciebie robimy w{" "}
                <span className="italic text-[var(--beautix-pink)]">BEAUTIX</span>
              </h2>
            </div>
          </div>

          <div className="divide-y divide-[var(--beautix-line)] border-y border-[var(--beautix-line)]">
            {service.items.map((it, i) => (
              <article
                key={it.name}
                data-testid={`service-item-${i}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 py-10 group"
              >
                <div className="md:col-span-1 text-[var(--beautix-gold)] font-serif-display text-3xl">
                  0{i + 1}
                </div>
                <div className="md:col-span-4">
                  <h3 className="font-serif-display text-2xl md:text-3xl group-hover:text-[var(--beautix-pink)] transition-colors">
                    {it.name}
                  </h3>
                  <span className="text-xs tracking-[0.25em] uppercase text-[var(--beautix-muted)] block mt-2">
                    {it.keyword}
                  </span>
                </div>
                <div className="md:col-span-6 text-[var(--beautix-muted)] leading-relaxed font-light">
                  {it.desc}
                </div>
                <div className="md:col-span-1 flex md:justify-end items-start">
                  <Link
                    to="/cennik"
                    aria-label={`Zobacz cenę ${it.name}`}
                    className="text-[var(--beautix-pink)]"
                  >
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Why us with second image */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <p className="eyebrow mb-4">Dlaczego BEAUTIX</p>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] mb-8">
            {finalSection?.title || "Dbałość o szczegół i pełna higiena."}
          </h2>
          <ul className="space-y-5">
            {(bullets || []).map((b) => (
              <li key={b} className="flex gap-4 items-start">
                <span className="mt-1.5 w-4 h-4 rounded-full bg-[var(--beautix-blush)] flex items-center justify-center text-[var(--beautix-pink)]">
                  <Check className="w-3 h-3" strokeWidth={2} />
                </span>
                <span className="text-base text-[var(--beautix-muted)] font-light leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-6 aspect-[4/5] overflow-hidden rounded-tr-[80px]">
          <img
            src={secondaryImage}
            alt={`${service.title} — realizacje BEAUTIX Katowice`}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      </section>

      <Reviews compact />

      {beforeCta || null}

      <CtaBanner
        title={`Umów ${service.title.toLowerCase()} w Katowicach`}
        subtitle="Zadzwoń, żeby zaplanować wizytę i dobrać zabieg do Twoich potrzeb."
      />
    </>
  );
}
